// 🔴 工具名稱整個不進前台（#1384）：里長看的是「為什麼做」不是「功能叫什麼」。
// 要追某一步用了哪支工具，走後台的對話稽核頁，那裡有完整事件流。

/** 意圖缺值時的固定文案。缺值罕見：舊事件、參數還在串流、模型送空字串。 */
const PURPOSE_FALLBACK = '資料查詢'

/** 串接用的中點。頓號表示並列的同類事物、讀起來像一句話；中點才看得出是幾件事。 */
const PURPOSE_SEPARATOR = ' · '

/** 畫面上那一行的主文。意圖缺席時退回固定文案，那一行不會空。 */
export const purposeText = (purpose: string): string => purpose || PURPOSE_FALLBACK

/**
 * 查無資料的判定（#1397）：有陣列欄位而且全是空的。
 * 沒有筆數可講時靠它把「查不到」說出來，否則那一支看起來像什麼都沒發生。
 */
const isEmptyResult = (value: Record<string, unknown>): boolean => {
  const lists = Object.values(value).filter(Array.isArray)
  return lists.length > 0 && lists.every((list) => list.length === 0)
}

/**
 * 工具結果的一句話摘要。看得到查回幾筆，使用者才判斷得出答案值不值得信。
 * 🔴 失敗只認 error 欄位（#1397），與伺服器 isToolResultFailed 同一條規則：
 * 兩邊規則一致，即時看到幾項失敗，重新整理後才會是同樣幾項。
 */
export const summarizeOutput = (output: unknown): { text: string; failed: boolean } | null => {
  if (output === null || typeof output !== 'object') return null
  const value = output as Record<string, unknown>
  // 失敗一律帶 error；message 是給人看的那一句，取得到就用它，比錯誤代碼好懂
  if (typeof value.error === 'string') {
    return { text: typeof value.message === 'string' ? value.message : value.error, failed: true }
  }
  // 🔴 沒有 error 的 message 不是失敗訊號：查不到在這個系統裡是常見的正常結果
  if (typeof value.message === 'string') return { text: value.message, failed: false }
  if (typeof value.total === 'number') return { text: `共 ${value.total} 筆`, failed: false }
  if (isEmptyResult(value)) return { text: '查無資料', failed: false }
  return { text: '完成', failed: false }
}

/** 模型自己寫的呼叫目的（#1362）。舊對話的歷史訊息沒有它，取不到就回空字串。 */
export const readPurpose = (input: unknown): string => {
  if (input === null || typeof input !== 'object') return ''
  const purpose = (input as { purpose?: unknown }).purpose
  return typeof purpose === 'string' ? purpose.trim() : ''
}

/**
 * 工具回應的四態（#1414）。🔴 與伺服器的 UserViewToolCall.outcome 同一組字串：
 * 兩邊詞彙一致，即時看到的那一態，重新整理後才會是同一態。
 */
export type ToolOutcome = 'ok' | 'failed' | 'denied' | 'needs_input'

const OUTCOMES: readonly string[] = ['ok', 'failed', 'denied', 'needs_input']

/**
 * 被拒絕那一支的固定文案。🔴 兩條路徑都用它：即時只知道「使用者按了不允許」、
 * 沒有伺服器那句說明，文案若取自訊息就會與重新整理後不一致（#1414）。
 */
const DENIED_NOTE = '未執行'

/** 待補充但工具沒寫問句時的固定文案。缺值罕見，那一格不會空著。 */
const NEEDS_INPUT_NOTE = '需要你補充資訊'

/** 工具寫給人看的那一句；沒有就是空字串。 */
const readMessage = (output: unknown): string => {
  const message = (output as { message?: unknown } | null)?.message
  return typeof message === 'string' ? message : ''
}

/**
 * 工具自己說「這不是資料，我需要你補一件事」的標記（#1414）。
 * 🔴 與伺服器 tools/core/result.ts 同一個字串，由測試釘住——UI 不 import
 * interface 套件（會把 grpc 拖進 bundle）。
 */
const isNeedsInputOutput = (output: unknown): boolean =>
  typeof output === 'object' && output !== null &&
  (output as { outcome?: unknown }).outcome === 'needs_input'

export interface ToolPartInput {
  state: string
  // 模型送出的引數。purpose 在參數收完那一刻就在裡面了，不必等工具執行完
  input?: unknown
  output?: unknown
  errorText?: string
  // 續談重建的卡片帶著伺服器判定的成敗（#1305）；即時串流沒有它，看 state 與摘要
  metaFailed?: unknown
  // 伺服器判定的四態（#1414）。舊版伺服器沒有它，退回 metaFailed 與 state
  metaOutcome?: unknown
}

export interface ToolStatus {
  /** 主文：模型寫的這一次為什麼要做（#1384）。缺席時是空字串，由 purposeText 補 */
  purpose: string
  note: string
  running: boolean
  failed: boolean
  /** 四態判定（#1414）。failed 是它的投影，留著是因為樣式與既有呼叫端都在用 */
  outcome: ToolOutcome
}

/**
 * 這一支落在哪一態（#1414）。伺服器判定優先，其次是 SDK 的拒絕狀態，
 * 最後才退回 #1397 的成敗規則——那條規則本身一個字都沒動。
 */
const readOutcome = (
  part: ToolPartInput, summary: { failed: boolean } | null,
): ToolOutcome => {
  if (typeof part.metaOutcome === 'string' && OUTCOMES.includes(part.metaOutcome)) {
    return part.metaOutcome as ToolOutcome
  }
  // 即時：使用者按了不允許，SDK 把那一支停在這個狀態
  if (part.state === 'output-denied') return 'denied'
  if (isNeedsInputOutput(part.output)) return 'needs_input'
  if (typeof part.metaFailed === 'boolean') return part.metaFailed ? 'failed' : 'ok'
  return part.state === 'output-error' || summary?.failed ? 'failed' : 'ok'
}

/** 註記那一格寫什麼。第三態一律不用「失敗」字樣：它們不是失敗。 */
const readNote = (part: ToolPartInput, outcome: ToolOutcome, summaryText?: string): string => {
  if (part.state === 'input-streaming' || part.state === 'input-available') return '查詢中…'
  if (outcome === 'denied') return DENIED_NOTE
  // 🔴 取 message 而不是摘要：摘要對沒有筆數的結果會說「完成」，
  // 而這一支正好什麼都沒查完——那正是要避免的那句話
  if (outcome === 'needs_input') return readMessage(part.output) || NEEDS_INPUT_NOTE
  if (part.state === 'output-error') return part.errorText ?? '失敗'
  return summaryText ?? '完成'
}

/** 把一支工具的原始狀態化為畫面要的那幾件事：呼叫目的、註記、四態。 */
export const describeTool = (part: ToolPartInput): ToolStatus => {
  const running = part.state === 'input-streaming' || part.state === 'input-available'
  const summary = part.state === 'output-available' ? summarizeOutput(part.output) : null
  const outcome = readOutcome(part, summary)
  return {
    purpose: readPurpose(part.input),
    note: readNote(part, outcome, summary?.text),
    running,
    failed: outcome === 'failed',
    outcome,
  }
}

export interface GroupSummary {
  /** 摘要行主文，例如「查詢里民欄位有哪些 · 統計符合條件的里民人數」 */
  text: string
  /** 🔴 狀態不進摺疊：失敗、待補充與執行中要在收合時就看得見 */
  flag: string | null
  running: boolean
  failed: boolean
  /** 有待補充或未執行的那幾支（#1414）。它們不是失敗，樣式上也不該是紅的 */
  attention: boolean
}

/** 最新那一支還在跑的工具位置；都跑完了回 -1。摺疊行的文案與耗時都看它。 */
export const latestRunningIndex = (tools: ToolStatus[]): number =>
  tools.reduce((latest, tool, index) => (tool.running ? index : latest), -1)

/**
 * 收合那一行寫什麼（#1384）：整組所有工具的意圖，以中點串接、截成一行。
 * 進行中與完成後文案相同，狀態只靠動畫與 flag 區分，完成那一瞬間不會跳字。
 *
 * 🔴 不去重：意圖是模型針對每一次呼叫各自寫的，字面相同不代表做的是同一件事
 * （同一支統計工具可能分別統計不同條件），去重會讓人以為只呼叫了一次。
 */
export const summarizeToolGroup = (tools: ToolStatus[]): GroupSummary => {
  const countOf = (outcome: ToolOutcome): number =>
    tools.filter((tool) => tool.outcome === outcome).length
  const failedCount = countOf('failed')
  const needsInputCount = countOf('needs_input')
  const deniedCount = countOf('denied')
  const running = latestRunningIndex(tools) >= 0
  // 缺值的那幾支跳過而不補「資料查詢」：剛開始收參數的那零點幾秒還沒有意圖，
  // 補位會讓這一行多出一段隨串流跳動的雜訊。整組都缺才整行退回固定文案。
  const purposes = tools.map((tool) => tool.purpose).filter(Boolean)
  const text = purposes.length > 0 ? purposes.join(PURPOSE_SEPARATOR) : PURPOSE_FALLBACK
  // 🔴 旗標一次只講一件事，順序＝使用者要先看到的順序（#1414）：
  // 真的失敗最急，其次是等著他回答的那一支，再來是他自己按掉的那一支。
  const flag = failedCount > 0 ? `${failedCount} 項失敗` :
    needsInputCount > 0 ? `${needsInputCount} 項待補充` :
      deniedCount > 0 ? `${deniedCount} 項未執行` :
        running ? '執行中…' : null
  return {
    text,
    flag,
    running,
    failed: failedCount > 0,
    attention: needsInputCount + deniedCount > 0,
  }
}

/** 秒與毫秒的分界。工具多半跑幾十毫秒，全部壓成 0.0 秒等於沒有數字。 */
const SECOND = 1000

/**
 * 顯示前先量化到看得見的那一位（#1412）：未滿一秒到毫秒、一秒以上到 0.1 秒。
 * 合計吃的是量化後的值，畫面上的加法才會成立。
 */
const quantize = (ms: number): number => {
  const safe = Math.max(0, ms)
  return safe < SECOND ? Math.round(safe) : Math.round(safe / 100) * 100
}

/**
 * 耗時的顯示字樣。未滿一秒改用毫秒（#1412）：三支各 40 毫秒還顯示 0.0 秒時，
 * 合計的 0.1 秒看起來就像憑空冒出來的。
 */
export const formatDuration = (ms: number): string => {
  const value = quantize(ms)
  return value < SECOND ? `${value} 毫秒` : `${(value / SECOND).toFixed(1)} 秒`
}

/**
 * 摺疊列那一個數字的字樣（#1399）。🔴 它只量到工具執行時間的加總，
 * 不是使用者等了多久——光禿禿的「1.2 秒」會被讀成總等待，所以帶限定詞。
 * 工具卡不用它：那個位置就是那一支的執行時間，加字只是雜訊。
 */
export const groupDurationText = (ms: number): string => `查詢合計 ${formatDuration(ms)}`

const SETTLED_STATES = ['output-available', 'output-error', 'output-denied']

/**
 * 這一支該不該有耗時（#1406）。🔴 被拒絕的不該有：那不是「執行了 0 毫秒」，
 * 是根本沒執行；前端計時器在那個狀態下量到的是使用者猶豫了多久。
 */
export const isElapsedApplicable = (state: string): boolean => state !== 'output-denied'

export interface DurationTracker {
  /** 回這一支目前的耗時（ms）；不知道起點（重新整理後的歷史）就回 null */
  observe: (id: string, state: string, now: number) => number | null
}

/**
 * 執行中那一支的即時估計：參數收完到拿到輸出的時間差。
 * 🔴 從 input-available 起算而不是第一眼（#1399）：伺服器記的是工具執行了多久，
 * 兩邊量同一段，完成那一刻換成伺服器的值才不會往下跳。歷史量不到，回 null。
 */
export const createDurationTracker = (): DurationTracker => {
  const started = new Map<string, number>()
  const settled = new Map<string, number>()
  return {
    observe: (id, state, now) => {
      const done = SETTLED_STATES.includes(state)
      if (settled.has(id)) return settled.get(id) ?? null
      if (!started.has(id)) {
        // 第一眼就已經結束＝這是載進來的歷史，沒有起點可量
        if (done) return null
        // 參數還在串流，工具還沒開始跑：那一段不算它的耗時
        if (state === 'input-streaming') return null
        started.set(id, now)
        return 0
      }
      const elapsed = now - (started.get(id) ?? now)
      if (done) settled.set(id, elapsed)
      return elapsed
    },
  }
}

/**
 * 這一支到底花了多久（#1399）。伺服器記下的值優先：它在事件流裡，
 * 重新整理後還在，而且即時串流也掛著同一份——兩邊因此是同一個數字。
 */
export const resolveElapsed = (recorded: unknown, tracked: number | null): number | null => {
  if (typeof recorded === 'number' && Number.isFinite(recorded) && recorded >= 0) return recorded
  return tracked
}

/**
 * 整組的總耗時＝各支「顯示值」的加總（#1412）。加的是量化後的值而不是原始毫秒，
 * 摺疊列那個數字才會等於展開後那幾個數字相加，不會出現合計 0.1、成員全 0.0。
 * 🔴 一支都算不出來時回 null——那一行不顯示而不是顯示 0：舊對話正是這種。
 */
export const totalElapsed = (values: (number | null)[]): number | null => {
  const known = values.filter((value): value is number => value !== null)
  return known.length > 0 ? known.reduce((sum, value) => sum + quantize(value), 0) : null
}

export type GroupedPart<T> =
  | { kind: 'tools'; tools: T[] }
  | { kind: 'other'; part: T; index: number }

/**
 * AI SDK 每一步開頭推的隱形 part（#1417）。它在畫面上什麼都不畫，
 * 而歷史那條逐事件投影根本不知道步界——留著它切分組，兩邊就對不起來。
 * 🔴 本票的決定：步界不進使用者畫面（agent view 與稽核頁仍照步分組）。
 */
export const isStepBoundary = (part: unknown): boolean =>
  typeof part === 'object' && part !== null &&
  (part as { type?: unknown }).type === 'step-start'

/**
 * 這一段文字值不值得畫成氣泡（#1437）。模型在兩次查詢之間常只吐一個換行，
 * 空字串擋得掉、只有空白的擋不掉——不濾就是一顆看不出內容的空氣泡。
 */
export const hasVisibleText = (text: string): boolean => text.trim().length > 0

/**
 * 只收「連續」的工具 part，文字或附件打斷就切開一組。
 * 🔴 整則訊息壓成一行會讓「先查、講一句、再算」的推導順序消失，那順序本身就是資訊。
 * isIgnored 命中的 part 當作不存在：既不成組也不切組，但仍佔掉原始索引。
 */
export const groupConsecutiveTools = <T>(
  parts: T[],
  isTool: (part: T) => boolean,
  isIgnored: (part: T) => boolean = () => false,
): GroupedPart<T>[] => {
  const grouped: GroupedPart<T>[] = []
  let current: T[] | null = null
  parts.forEach((part, index) => {
    if (isIgnored(part)) return
    if (isTool(part)) {
      if (current) current.push(part)
      else {
        current = [part]
        grouped.push({ kind: 'tools', tools: current })
      }
      return
    }
    current = null
    grouped.push({ kind: 'other', part, index })
  })
  return grouped
}
